import type { Erc20Transfer, LowerHex } from '../../types'
import type { InternalTransactionWithIndex } from '../../types/internalTransaction'
import type { TransactionIndex } from '../../types/transactionIndex'
import { createInternalTransactionId } from '../../utils/createInternalTransactionId'
import type { Explorer, HeadersInput, LogWithBlockNumber } from '../types'
import { createEtherscanV2Client } from './client'
import { getBlockNumberByTimestamp } from './getBlockNumberByTimestamp'
import { getContractCreatorAndCreationTxHash } from './getContractCreatorAndCreationTxHash'
import { getContractSourceCodeForVerifiedContract } from './getContractSourceCodeForVerifiedContract'
import { getEventLogsByAddressFilteredByTopics } from './getEventLogsByAddressFilteredByTopics'
import { getInternalTransactionsByTransactionHash } from './getInternalTransactionsByTransactionHash'
import { getListOfErc20TokenTransferEventsByAddress } from './getListOfErc20TokenTransferEventsByAddress'
import { getListOfErc721TokenTransferEventsByAddress } from './getListOfErc721TokenTransferEventsByAddress'
import { getListOfErc1155TokenTransferEventsByAddress } from './getListOfErc1155TokenTransferEventsByAddress'
import { getListOfInternalTransactionsByAddress } from './getListOfInternalTransactionsByAddress'
import { getListOfNormalTransactionsByAddress } from './getListOfNormalTransactionsByAddress'

/**
 * Resolves headers from either a static object or a function that returns headers.
 * This allows for dynamic header generation (e.g., refreshing auth tokens).
 */
const resolveHeaders = async (
  headers: HeadersInput,
): Promise<Record<string, string> | undefined> => {
  if (headers === undefined) {
    return undefined
  }
  if (typeof headers === 'function') {
    return await headers()
  }
  return headers
}

type CreateEtherscanV2Parameters = {
  name: string
  baseUrl: string
  chainId: number
  apiKey?: string
  proxyUrl?: string
  pageSize?: number
}

export const createEtherscanV2 = ({
  name,
  baseUrl,
  chainId,
  apiKey,
  proxyUrl,
  pageSize = 10_000,
}: CreateEtherscanV2Parameters): Explorer => {
  const client = createEtherscanV2Client({
    chainId,
    apiKey,
    proxyUrl,
  })

  return {
    name,
    baseUrl,
    getAddressInternalTransactions: async ({
      address,
      fromBlock,
      toBlock,
      headers,
    }) => {
      const internalTransactions = new Map<
        string,
        InternalTransactionWithIndex
      >()
      let startblock = fromBlock
      const endblock = toBlock
      while (true) {
        const resolvedHeaders = await resolveHeaders(headers)
        const result = await getListOfInternalTransactionsByAddress({
          client,
          address,
          startblock,
          endblock,
          offset: pageSize,
          headers: resolvedHeaders,
        })
        for (const transaction of result) {
          const id = createInternalTransactionId(transaction)
          if (!internalTransactions.has(id)) {
            internalTransactions.set(id, transaction)
          }
        }
        if (result.length < pageSize) {
          break
        }
        const maxBlockNumber = Math.max(...result.map((x) => x.blockNumber))
        startblock = maxBlockNumber
      }
      return [...internalTransactions.values()]
    },
    getAddressTransactionIndexes: async ({
      address,
      fromBlock,
      toBlock,
      headers,
    }) => {
      const indexes = new Map<LowerHex, TransactionIndex>()
      let startblock = fromBlock
      const endblock = toBlock
      while (true) {
        const resolvedHeaders = await resolveHeaders(headers)
        const result = await getListOfNormalTransactionsByAddress({
          client,
          address,
          startblock,
          endblock,
          offset: pageSize,
          headers: resolvedHeaders,
        })
        for (const index of result) {
          if (!indexes.has(index.hash)) {
            indexes.set(index.hash, index)
          }
        }
        if (result.length < pageSize) {
          break
        }
        const maxBlockNumber = Math.max(...result.map((x) => x.blockNumber))
        startblock = maxBlockNumber
      }
      return [...indexes.values()]
    },
    getAddressTokenTransferIndexes: async ({
      address,
      fromBlock,
      toBlock,
      headers,
    }) => {
      const indexes = new Map<LowerHex, TransactionIndex>()
      const erc20Transfers: Erc20Transfer[] = []
      const endblock = toBlock

      // ERC20
      let startblock = fromBlock
      while (true) {
        const resolvedHeaders = await resolveHeaders(headers)
        const { indexes: resultIndexes, erc20Transfers: resultErc20Transfers } =
          await getListOfErc20TokenTransferEventsByAddress({
            client,
            address,
            startblock,
            endblock,
            offset: pageSize,
            headers: resolvedHeaders,
          })
        for (const index of resultIndexes) {
          if (!indexes.has(index.hash)) {
            indexes.set(index.hash, index)
          }
        }
        erc20Transfers.push(...resultErc20Transfers)
        if (resultIndexes.length < pageSize) {
          break
        }
        const maxBlockNumber = Math.max(
          ...resultIndexes.map((x) => x.blockNumber),
        )
        startblock = maxBlockNumber
      }

      // ERC721
      startblock = fromBlock
      while (true) {
        const resolvedHeaders = await resolveHeaders(headers)
        const result = await getListOfErc721TokenTransferEventsByAddress({
          client,
          address,
          startblock,
          endblock,
          offset: pageSize,
          headers: resolvedHeaders,
        })
        for (const index of result) {
          if (!indexes.has(index.hash)) {
            indexes.set(index.hash, index)
          }
        }
        if (result.length < pageSize) {
          break
        }
        const maxBlockNumber = Math.max(...result.map((x) => x.blockNumber))
        startblock = maxBlockNumber
      }

      // ERC1155
      startblock = fromBlock
      while (true) {
        const resolvedHeaders = await resolveHeaders(headers)
        const result = await getListOfErc1155TokenTransferEventsByAddress({
          client,
          address,
          startblock,
          endblock,
          offset: pageSize,
          headers: resolvedHeaders,
        })
        for (const index of result) {
          if (!indexes.has(index.hash)) {
            indexes.set(index.hash, index)
          }
        }
        if (result.length < pageSize) {
          break
        }
        const maxBlockNumber = Math.max(...result.map((x) => x.blockNumber))
        startblock = maxBlockNumber
      }

      return {
        indexes: [...indexes.values()],
        erc20Transfers,
      }
    },
    getBlockNumberOfTimestamp: async ({
      timestamp,
      headers,
    }): Promise<number> => {
      const resolvedHeaders = await resolveHeaders(headers)
      return getBlockNumberByTimestamp({
        client,
        timestamp: Math.floor(timestamp / 1000),
        headers: resolvedHeaders,
      })
    },
    getContract: async ({ address, headers }) => {
      const resolvedHeaders = await resolveHeaders(headers)
      return getContractSourceCodeForVerifiedContract({
        client,
        address,
        headers: resolvedHeaders,
      })
    },
    getContractCreations: async ({ addresses, headers }) => {
      const resolvedHeaders = await resolveHeaders(headers)
      return getContractCreatorAndCreationTxHash({
        client,
        contractaddresses: addresses,
        headers: resolvedHeaders,
      })
    },
    getEventLogs: async ({
      address,
      topic0,
      fromBlock: fromBlockParam,
      toBlock,
      headers,
    }) => {
      const logs: LogWithBlockNumber[] = []
      let fromBlock = fromBlockParam ?? 0
      const maxPageSize = 10_000
      while (true) {
        const resolvedHeaders = await resolveHeaders(headers)
        const result = await getEventLogsByAddressFilteredByTopics({
          client,
          address,
          topic0,
          fromBlock,
          toBlock,
          headers: resolvedHeaders,
        })
        logs.push(...result)
        if (result.length < maxPageSize) {
          break
        }
        const maxBlockNumber = Math.max(...result.map((x) => x.blockNumber))
        fromBlock = maxBlockNumber
      }
      return logs
    },
    getInternalTransactionOfTransaction: async ({ hash, headers }) => {
      const resolvedHeaders = await resolveHeaders(headers)
      return getInternalTransactionsByTransactionHash({
        client,
        txhash: hash,
        headers: resolvedHeaders,
      })
    },
  }
}
